import { ProfileChannel, ProfileData, ProfileOwner, ProfileResponse } from "@/types/profile";
import {
  filterSupportedProfileChannels,
  getParentChannels,
  PARENT_CHANNEL_NAMES,
  formatChannelLabel
} from "@/utilities/jobs/profileUtils";
import { clampChannelReleaseMode } from "@/utilities/profiles/channelFormUtils";
import { toProfileYamlChannel } from "@/utilities/profiles/profileYamlUtils";
import {
  PROFILE_PLATFORM,
  PROFILE_PLATFORM_OPTIONS,
  PROFILE_SIDEBAR_NODE,
  DEFAULT_PROFILE_CHANNEL_NAME
} from "@/constants/profileConstants";

const CANONICAL_PROFILE_PLATFORMS = [
  PROFILE_PLATFORM.WINDOWS,
  PROFILE_PLATFORM.MAC,
  PROFILE_PLATFORM.LINUX
] as const;

const PROFILE_PLATFORM_BY_KEY: Record<string, (typeof CANONICAL_PROFILE_PLATFORMS)[number]> = {
  windows: PROFILE_PLATFORM.WINDOWS,
  mac: PROFILE_PLATFORM.MAC,
  linux: PROFILE_PLATFORM.LINUX
};

/** Normalizes profile platforms to `PROFILE_PLATFORM.*` (`Windows`, `Mac`, `Linux`) for form state and API. */
export const normalizeProfilePlatformValues = (platforms: string[] | undefined): string[] => {
  if (!platforms?.length) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  platforms.forEach((platform) => {
    const trimmed = platform?.trim();
    if (!trimmed) return;

    const canonical =
      (CANONICAL_PROFILE_PLATFORMS as readonly string[]).find((value) => value === trimmed) ??
      PROFILE_PLATFORM_BY_KEY[trimmed.toLowerCase()];

    if (!canonical || seen.has(canonical)) return;

    seen.add(canonical);
    normalized.push(canonical);
  });

  return normalized;
};

/** Human-readable platform labels for review and dashboard. */
export const formatProfilePlatformLabels = (platforms: string[] | undefined): string => {
  const normalized = normalizeProfilePlatformValues(platforms);
  if (!normalized.length) return "-";
  return normalized
    .map(
      (platform) =>
        PROFILE_PLATFORM_OPTIONS.find((option) => option.value === platform)?.label ??
        formatChannelLabel(platform)
    )
    .join(", ");
};

/** Normalizes channel state on submit (e.g. release mode clamping). */
export const withChannelLockedFieldDefaults = (
  channel: ProfileChannel,
  releaseTypes: string[] = []
): ProfileChannel => clampChannelReleaseMode(channel, releaseTypes);

/*
 * Reserved — re-enable with TTL / Side by Side / Promotion Allowed in UI + payload:
 *
 * import { CHANNEL_INFO_LOCKED_FIELD_DEFAULTS } from "@/constants/profileConstants";
 *
 * export const withChannelLockedFieldDefaults = (channel: ProfileChannel): ProfileChannel =>
 *   clampChannelReleaseMode({
 *     ...channel,
 *     TTL: CHANNEL_INFO_LOCKED_FIELD_DEFAULTS.ttl,
 *     SideBySide: CHANNEL_INFO_LOCKED_FIELD_DEFAULTS.sideBySide,
 *     PromotionAllowed: CHANNEL_INFO_LOCKED_FIELD_DEFAULTS.promotionAllowed
 *   });
 */

/** Builds a default profile name from PLC, release types, and platform. */
export const buildAutoProfileName = (plc: string, releaseTypes: string[], platforms: string[]): string => {
  if (!plc) return "";
  const typePart =
    releaseTypes.length > 0
      ? releaseTypes.map((type) => type.replace(/[() -]+/g, "")).join("_")
      : "Profile";
  const platformPart =
    platforms.length === 1 ? formatChannelLabel(platforms[0]) : platforms.map((p) => formatChannelLabel(p)).join("_");
  const segments = [typePart];
  if (platformPart) segments.push(platformPart);
  segments.push(plc);

  return segments.join("_");
};

export const createDefaultChannel = (name: string): ProfileChannel =>
  withChannelLockedFieldDefaults({
    Name: name,
    Enabled: true,
    ReleaseMode: "manual",
    Publish: {},
    Release: {}
  });

/** Release is always seeded for new profiles. */
export const createDefaultProfileChannels = (): ProfileChannel[] => [createDefaultChannel(DEFAULT_PROFILE_CHANNEL_NAME)];

export const isDefaultReleaseChannel = (channelName: string): boolean =>
  channelName.toLowerCase() === DEFAULT_PROFILE_CHANNEL_NAME;

/** Parent channels from PARENT_CHANNEL_NAMES not yet present in the profile. */
export const getAvailableParentChannelNames = (channels: ProfileChannel[]): string[] => {
  const present = new Set(filterSupportedProfileChannels(channels).map((c) => c.Name.toLowerCase()));
  return PARENT_CHANNEL_NAMES.filter((name) => !present.has(name));
};

export interface ProfileOwnerFormEntry {
  name: string;
  email: string;
}

export const createDefaultOwner = (): ProfileOwnerFormEntry => ({ name: "", email: "" });

/** Keeps the first owner row for each non-empty email (case-insensitive). */
export const dedupeOwnerEmails = (owners: ProfileOwnerFormEntry[]): ProfileOwnerFormEntry[] => {
  const seen = new Set<string>();
  return owners.filter((owner) => {
    const normalized = owner.email.trim().toLowerCase();
    if (!normalized) return true;
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

/** Owner row matching the signed-in user (prefilled on create; not editable or removable). */
export const isProfileOwnerLocked = (
  owner: ProfileOwnerFormEntry,
  lockedOwnerEmail: string | undefined
): boolean => {
  const locked = lockedOwnerEmail?.trim().toLowerCase();
  if (!locked) return false;
  return owner.email.trim().toLowerCase() === locked;
};

export interface SubmitJobProfileFormState {
  plc: string;
  profileName: string;
  owners: ProfileOwnerFormEntry[];
  releaseTypes: string[];
  platforms: string[];
  channels: ProfileChannel[];
}

export interface ProfileWriteIdentity {
  plc: string;
  profileName: string;
}

export const buildProfileWriteBody = (
  form: SubmitJobProfileFormState,
  identity?: ProfileWriteIdentity,
  editMode: boolean = false
): { plc: string; profileName: string; ProfileData: ProfileData } => {
  const plc = identity?.plc ?? form.plc;
  const profileName = identity?.profileName ?? form.profileName;
  const owners = form.owners
    .filter((owner) => owner.email.trim().length > 0)
    .map((owner) => ({
      Email: owner.email.trim(),
      Name: owner.name.trim() || owner.email.split("@")[0] || ""
    }));

  return {
    plc,
    profileName: editMode ? profileName : `${profileName}.yaml`,
    ProfileData: {
      Profile: {
        PLC: form.plc === "_DEFAULT" ? null : form.plc,
        ReleaseType: form.releaseTypes,
        Platform: normalizeProfilePlatformValues(form.platforms),
        ...(owners.length > 0 ? { Owners: owners } : {})
      },
      Channels: form.channels
        .map((channel) => withChannelLockedFieldDefaults(channel, form.releaseTypes))
        .map((channel) => toProfileYamlChannel(channel)) as unknown as ProfileChannel[]
    }
  };
};

export interface ProfileFormSaveContext {
  editMode: boolean;
  editPlc?: string;
  editProfileName?: string;
}

/** Stable JSON of the payload that would be sent on Save (create or patch). */
export const buildProfileFormSaveSnapshot = (
  form: SubmitJobProfileFormState,
  context: ProfileFormSaveContext
): string => {
  const identity = context.editMode
    ? {
      plc: context.editPlc || form.plc,
      profileName: context.editProfileName || form.profileName
    }
    : undefined;
  return JSON.stringify(buildProfileWriteBody(form, identity, context.editMode));
};

export const isProfileFormDirty = (
  form: SubmitJobProfileFormState,
  context: ProfileFormSaveContext,
  baselineSnapshot: string | undefined
): boolean => {
  if (!baselineSnapshot) {
    return false;
  }
  return buildProfileFormSaveSnapshot(form, context) !== baselineSnapshot;
};

/** Ordered sidebar/stepper node ids: General → parent channels (+ sub-channels) → Review. */
export const buildProfileNavSequence = (channels: ProfileChannel[]): string[] => {
  const sequence: string[] = [PROFILE_SIDEBAR_NODE.GENERAL];
  getParentChannels(channels).forEach((parent) => {
    sequence.push(channelNodeId(parent.Name));
  });
  sequence.push(PROFILE_SIDEBAR_NODE.REVIEW);
  return sequence;
};

/** Sidebar node id for a channel row being removed (parent or sub). */
export const deletedChannelNodeId = (channelName: string): string => channelNodeId(channelName);

/**
 * After delete, navigate to the step immediately before the removed node in the pre-delete
 * wizard order, if that step still exists; otherwise the first remaining channel or General Info.
 */
export const getNavTargetAfterChannelDelete = (
  channelsBefore: ProfileChannel[],
  channelsAfter: ProfileChannel[],
  deletedChannelName: string
): string => {
  const deletedNodeId = deletedChannelNodeId(deletedChannelName);
  const sequenceBefore = buildProfileNavSequence(channelsBefore);
  const sequenceAfter = new Set(buildProfileNavSequence(channelsAfter));
  const deletedIndex = sequenceBefore.indexOf(deletedNodeId);

  if (deletedIndex > 0) {
    for (let i = deletedIndex - 1; i >= 0; i -= 1) {
      const candidate = sequenceBefore[i];
      if (sequenceAfter.has(candidate)) {
        return candidate;
      }
    }
  }

  const firstRemainingChannel = buildProfileNavSequence(channelsAfter).find(
    (nodeId) => nodeId !== PROFILE_SIDEBAR_NODE.GENERAL && nodeId !== PROFILE_SIDEBAR_NODE.REVIEW
  );
  return firstRemainingChannel ?? PROFILE_SIDEBAR_NODE.GENERAL;
};

export const channelNodeId = (channelName: string): string => `${PROFILE_SIDEBAR_NODE.CHANNEL_PREFIX}${channelName}`;

export const parseSidebarNodeId = (nodeId: string): { type: "general" | "review" | "channel"; name?: string } => {
  if (nodeId === PROFILE_SIDEBAR_NODE.GENERAL) return { type: "general" };
  if (nodeId === PROFILE_SIDEBAR_NODE.REVIEW) return { type: "review" };
  if (nodeId === PROFILE_SIDEBAR_NODE.CHANNEL_INFO) return { type: "channel", name: DEFAULT_PROFILE_CHANNEL_NAME };
  if (nodeId.startsWith(PROFILE_SIDEBAR_NODE.CHANNEL_PREFIX)) {
    return { type: "channel", name: nodeId.slice(PROFILE_SIDEBAR_NODE.CHANNEL_PREFIX.length) };
  }
  return { type: "general" };
};

export const PROFILE_EDIT_ACCESS_DENIED_MESSAGE =
  "You are not allowed to edit this job profile. Only profile owners or admins can make changes.";

export const PROFILE_VIEW_ONLY_BANNER_MESSAGE =
  "This profile is read-only. You can view all details, but only profile owners or admins can make changes.";

const isBlankOwnerEmail = (email: string | null | undefined): boolean => !email?.trim();

/** True when any owner row from GET /profile has a missing or blank email. */
export const profileHasBlankOwnerEmail = (owners: ProfileOwner[] | undefined): boolean =>
  (owners ?? []).some((owner) => isBlankOwnerEmail(owner.Email));

/**
 * Edit is allowed for admins and listed owners. Profiles with blank owner emails
 * are editable by admins only.
 */
export const canUserEditProfile = (
  owners: ProfileOwner[] | undefined,
  userEmail: string | undefined,
  isAdmin: boolean
): boolean => {
  const normalizedUser = userEmail?.trim().toLowerCase();
  if (!normalizedUser) return false;

  if (profileHasBlankOwnerEmail(owners)) {
    return isAdmin;
  }

  const ownerEmails = (owners ?? [])
    .map((owner) => owner.Email?.trim().toLowerCase())
    .filter((email): email is string => Boolean(email));

  return isAdmin || ownerEmails.includes(normalizedUser);
};

/** Hydrates form state from a GET /profile response. */
export const profileResponseToFormState = (response: ProfileResponse): SubmitJobProfileFormState => {
  const { ProfileData, plc, profileName } = response;
  const owners = ProfileData.Profile.Owners?.length
    ? ProfileData.Profile.Owners.map((owner) => ({
      name: owner.Name ?? "",
      email: owner.Email ?? ""
    }))
    : [createDefaultOwner()];
  const releaseTypes = ProfileData.Profile.ReleaseType ?? [];
  return {
    plc,
    profileName,
    owners,
    releaseTypes,
    platforms: normalizeProfilePlatformValues(ProfileData.Profile.Platform),
    channels: filterSupportedProfileChannels(ProfileData.Channels).map((channel) =>
      withChannelLockedFieldDefaults(channel, releaseTypes)
    )
  };
};
